import { useMemo, useState } from 'react'
import { Search, Users as UsersIcon } from 'lucide-react'
import { Button, EmptyState, Input, Select, Spinner, useToast } from '@bb/ui'
import { de } from '../../i18n'
import { selectAccounts, type SortDirection, type UserFilters, type UserSortField } from './accountsTable'
import { AccountDetailDialog } from './AccountDetailDialog'
import { DeleteAccountDialog } from './DeleteAccountDialog'
import { deletionSuccessMessage } from './deletion'
import { UsersTable } from './UsersTable'
import { useAdminUsers } from './useUsers'
import type { AccountListItem, DeletionOutcome } from './types'

// Radix's Select rejects an empty string as an item value (it is reserved
// internally for "cleared"), so the "Alle" option needs its own sentinel
// rather than ''.
const ALL = 'all'

const ROLE_OPTIONS = [
  { value: ALL, label: de.common.all },
  { value: 'owner', label: de.users.role.owner },
  { value: 'member', label: de.users.role.member },
]

const ADMIN_OPTIONS = [
  { value: ALL, label: de.common.all },
  { value: 'true', label: de.users.adminFilter.onlyAdmins },
  { value: 'false', label: de.users.adminFilter.onlyNonAdmins },
]

export function UsersPage() {
  const toast = useToast()
  const users = useAdminUsers()

  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState(ALL)
  const [adminFilter, setAdminFilter] = useState(ALL)
  const [sort, setSort] = useState<UserSortField>('createdAt')
  const [dir, setDir] = useState<SortDirection>('desc')

  const [detailId, setDetailId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const filters: UserFilters = useMemo(
    () => ({
      role: roleFilter === ALL ? null : (roleFilter as 'owner' | 'member'),
      isAdmin: adminFilter === ALL ? null : adminFilter === 'true',
    }),
    [roleFilter, adminFilter],
  )

  const accounts = users.data?.users ?? []
  const visible = useMemo(
    () => selectAccounts(accounts, { search, filters, sort, dir }),
    [accounts, search, filters, sort, dir],
  )
  const activeFilterCount = (roleFilter !== ALL ? 1 : 0) + (adminFilter !== ALL ? 1 : 0)

  function toggleSort(field: UserSortField) {
    if (sort === field) setDir((current) => (current === 'asc' ? 'desc' : 'asc'))
    else {
      setSort(field)
      setDir('asc')
    }
  }

  function clearFilters() {
    setSearch('')
    setRoleFilter(ALL)
    setAdminFilter(ALL)
  }

  function handleDeleted(outcome: DeletionOutcome, person: { firstName: string; lastName: string }) {
    if (deletingId === detailId) setDetailId(null)
    setDeletingId(null)
    toast({ tone: 'success', title: deletionSuccessMessage(outcome.scope, person.firstName, person.lastName) })
  }

  return (
    <div className="p-6">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-ink">{de.users.title}</h1>

        <div className="relative w-full flex-1 sm:w-auto sm:min-w-56">
          <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-faint" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={de.users.search}
            aria-label={de.users.search}
            className="bg-sunken pl-8"
          />
        </div>

        <Select value={roleFilter} onValueChange={setRoleFilter} options={ROLE_OPTIONS} className="w-40" />
        <Select value={adminFilter} onValueChange={setAdminFilter} options={ADMIN_OPTIONS} className="w-48" />

        <span className="text-xs text-ink-faint">
          {visible.length} / {accounts.length}
        </span>
      </header>

      {users.isPending ? (
        <div className="flex justify-center py-24"><Spinner className="size-6" /></div>
      ) : accounts.length === 0 ? (
        <EmptyState icon={UsersIcon} title={de.users.empty.title} description={de.users.empty.description} />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Search}
          title={de.users.noResults.title}
          description={de.users.noResults.description}
          action={activeFilterCount > 0 || search ? (
            <Button variant="secondary" onClick={clearFilters}>{de.common.clear}</Button>
          ) : undefined}
        />
      ) : (
        <UsersTable
          accounts={visible}
          sort={sort}
          dir={dir}
          onSort={toggleSort}
          onSelect={(account: AccountListItem) => setDetailId(account.id)}
          onViewDetails={(account: AccountListItem) => setDetailId(account.id)}
          onDelete={(account: AccountListItem) => setDeletingId(account.id)}
        />
      )}

      <AccountDetailDialog userId={detailId} onOpenChange={(open) => !open && setDetailId(null)} />
      <DeleteAccountDialog
        userId={deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onDeleted={handleDeleted}
      />
    </div>
  )
}
